import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { expireFrontendSession } from "@/lib/api";
import { moduleForPath } from "@/lib/roleMenus";

const HEARTBEAT_MS = 30_000;
const ACTIVE_RECENCY_MS = 120_000;
const SERVER_PULSE_THROTTLE_MS = 15_000;
const ACTIVITY_STORAGE_KEY = "kd9-session-last-activity";
const TERMINATION_STORAGE_KEY = "kd9-session-terminated";

const fallbackModule = (pathname) => {
  const segment = String(pathname || "").split("/").filter(Boolean)[1];
  return segment || "inicio";
};

const mediaIsPlaying = () =>
  Array.from(document.querySelectorAll("video, audio")).some(
    (media) => !media.paused && !media.ended && media.readyState > 2
  );

const formatRemaining = (seconds) => {
  const safe = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const SessionActivityTracker = () => {
  const location = useLocation();
  const [policy, setPolicy] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [warningOpen, setWarningOpen] = useState(false);
  const lastInteraction = useRef(Date.now());
  const lastServerPulse = useRef(0);
  const timingOut = useRef(false);

  const sendActivity = useCallback(async (active, force = false) => {
    const timestamp = Date.now();
    if (active && !force && timestamp - lastServerPulse.current < SERVER_PULSE_THROTTLE_MS) {
      return;
    }
    if (active) lastServerPulse.current = timestamp;
    const pathname = window.location.pathname;
    try {
      await api.post("/api/auth/activity", {
        route: pathname,
        module: moduleForPath(pathname) || fallbackModule(pathname),
        active: Boolean(active),
      });
    } catch (_) {
      // Auth/session failures are handled centrally by the API interceptor.
    }
  }, []);

  const recordLocalActivity = useCallback((syncTabs = true, forceServer = false) => {
    const timestamp = Date.now();
    lastInteraction.current = timestamp;
    setWarningOpen(false);
    if (syncTabs) {
      try {
        window.localStorage.setItem(ACTIVITY_STORAGE_KEY, String(timestamp));
      } catch (_) {
        // Local storage can be unavailable in hardened/private browser contexts.
      }
    }
    sendActivity(true, forceServer);
  }, [sendActivity]);

  // Load the server-authoritative policy and align the local countdown with the
  // real session timestamp instead of assuming the page load is a new session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api/auth/session-policy");
        if (cancelled) return;
        const timeoutSeconds = Number(data?.timeout_seconds) || 1800;
        const remainingSeconds = Math.max(
          0,
          Math.min(timeoutSeconds, Number(data?.remaining_seconds) || 0)
        );
        // Align from the server's remaining duration rather than comparing
        // server/client wall clocks, which may have different clock skew.
        const serverAlignedLast =
          Date.now() - Math.max(0, timeoutSeconds - remainingSeconds) * 1000;
        let sharedLast = 0;
        try {
          sharedLast = Number(window.localStorage.getItem(ACTIVITY_STORAGE_KEY) || 0);
        } catch (_) {
          sharedLast = 0;
        }
        lastInteraction.current = Math.max(serverAlignedLast, sharedLast);
        setPolicy({
          timeoutSeconds,
          warningSeconds: Number(data?.warning_seconds) || 120,
        });
      } catch (_) {
        // A 401 here already triggers the central session-expiry flow.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Route changes are presence metadata only. They do not, by themselves,
  // extend the server inactivity window.
  useEffect(() => {
    sendActivity(false, true);
  }, [location.pathname, sendActivity]);

  // Real interaction only: mouse movement alone does not keep privileged
  // sessions alive. Pointer actions, keys and scrolling do.
  useEffect(() => {
    const onInteraction = () => recordLocalActivity(true, false);
    const events = ["pointerdown", "keydown", "touchstart", "wheel", "scroll"];
    events.forEach((eventName) =>
      window.addEventListener(eventName, onInteraction, { passive: true })
    );
    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, onInteraction));
    };
  }, [recordLocalActivity]);

  // Keep all tabs on the same session clock. One active tab protects the
  // session from a false timeout in a second open tab.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === TERMINATION_STORAGE_KEY && event.newValue) {
        try {
          const payload = JSON.parse(event.newValue);
          expireFrontendSession(payload?.reason || "expired", false);
        } catch (_) {
          expireFrontendSession("expired", false);
        }
        return;
      }
      if (event.key !== ACTIVITY_STORAGE_KEY || !event.newValue) return;
      const timestamp = Number(event.newValue);
      if (Number.isFinite(timestamp) && timestamp > lastInteraction.current) {
        lastInteraction.current = timestamp;
        setWarningOpen(false);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Active Academy media counts as valid activity. An abandoned static tab
  // does not. The server remains authoritative if the browser suspends timers.
  useEffect(() => {
    const tick = () => {
      if (mediaIsPlaying()) {
        const timestamp = Date.now();
        lastInteraction.current = timestamp;
        try {
          window.localStorage.setItem(ACTIVITY_STORAGE_KEY, String(timestamp));
        } catch (_) {}
        sendActivity(true, false);
        return;
      }
      if (document.hidden) return;
      if (Date.now() - lastInteraction.current <= ACTIVE_RECENCY_MS) {
        sendActivity(true, false);
      }
    };
    const timer = window.setInterval(tick, HEARTBEAT_MS);
    return () => window.clearInterval(timer);
  }, [sendActivity]);

  // Warning + client-side closure. This is UX only; backend auth.py independently
  // revokes sessions at the same role-specific boundary.
  useEffect(() => {
    if (!policy) return undefined;
    const timer = window.setInterval(() => {
      if (mediaIsPlaying()) {
        lastInteraction.current = Date.now();
      }

      const idleSeconds = Math.max(
        0,
        Math.floor((Date.now() - lastInteraction.current) / 1000)
      );
      const remaining = Math.max(0, policy.timeoutSeconds - idleSeconds);
      setRemainingSeconds(remaining);
      setWarningOpen(remaining > 0 && remaining <= policy.warningSeconds);

      if (remaining === 0 && !timingOut.current) {
        timingOut.current = true;
        (async () => {
          try {
            await api.post("/api/auth/logout?reason=inactivity");
          } catch (_) {
            // The server may have expired the session milliseconds earlier.
          } finally {
            expireFrontendSession("inactivity");
          }
        })();
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [policy]);

  const continueSession = async () => {
    recordLocalActivity(true, true);
    setWarningOpen(false);
  };

  if (!warningOpen || remainingSeconds === null) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/65 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kd9-session-warning-title"
      data-testid="session-inactivity-warning"
    >
      <div
        className="w-full max-w-md rounded-2xl border p-6 text-center shadow-2xl"
        style={{
          background: "var(--kd9-bg)",
          borderColor: "var(--kd9-gold)",
          color: "var(--kd9-text)",
        }}
      >
        <div
          className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border text-xl"
          style={{ borderColor: "var(--kd9-gold)", color: "var(--kd9-gold)" }}
          aria-hidden="true"
        >
          ⚡
        </div>
        <h2
          id="kd9-session-warning-title"
          className="font-cinzel text-lg font-semibold"
        >
          Tu sesión está por cerrarse
        </h2>
        <p className="mt-2 font-mont text-sm" style={{ color: "var(--kd9-text-soft)" }}>
          Detectamos inactividad. Por seguridad, KD9 cerrará tu sesión automáticamente.
        </p>
        <div
          className="my-5 font-mono text-3xl font-semibold tracking-widest"
          style={{ color: "var(--kd9-gold)" }}
          data-testid="session-inactivity-countdown"
        >
          {formatRemaining(remainingSeconds)}
        </div>
        <button
          type="button"
          onClick={continueSession}
          className="w-full rounded-xl px-4 py-3 font-mont text-sm font-semibold transition-transform hover:-translate-y-0.5"
          style={{ background: "var(--kd9-gold)", color: "#0a0a0a" }}
          data-testid="session-continue-button"
        >
          Continuar sesión
        </button>
      </div>
    </div>
  );
};

export default SessionActivityTracker;
